import { XBOT_BLOCK_TYPES, XBOT_MESSAGE_VERSION, XbotErrorBody, XbotMessageBlock } from "@dto/pyxbot-agent.dto";
import { Component, ComponentType, FC, lazy, LazyExoticComponent, ReactElement, ReactNode, Suspense } from "react";
import { LuChevronDown, LuChevronRight } from "react-icons/lu";
import { QuestionBlock } from "./blocks/QuestionBlock";
import { TableBlock } from "./blocks/TableBlock";
import { TextBlock } from "./blocks/TextBlock";
import { ComparisonBlock, ValueBlock } from "./blocks/ValueBlock";
import { XbotBlockHost, XbotBlockProps } from "./host";
import { blockNames, namedNodes, plainMarks, XbotPageNames } from "./markdownText";
import { BadgeLead, BadgeNotes } from "./parts";
import { calloutClass, CalloutIcon, DATA_BLOCK_TYPES, TonedPlainText, XbotBlockTone, XbotTone } from "./tone";

export type XbotBlockComponent = ComponentType<XbotBlockProps> | LazyExoticComponent<ComponentType<XbotBlockProps>>;

/** Block type -> the component that draws it. A type missing here is drawn as its fallback_text. */
export type XbotBlockRegistry = ReadonlyMap<string, XbotBlockComponent>;

// Charts and the relation tree are lazy-loaded; plain blocks aren't worth a separate request.
const TimeseriesBlock = lazy(() => import("./blocks/TimeseriesBlock")
  .then((loaded) => ({ default: loaded.TimeseriesBlock as ComponentType<XbotBlockProps> })));
const DistributionBlock = lazy(() => import("./blocks/DistributionBlock")
  .then((loaded) => ({ default: loaded.DistributionBlock as ComponentType<XbotBlockProps> })));
const RelationsBlock = lazy(() => import("./blocks/RelationsBlock")
  .then((loaded) => ({ default: loaded.RelationsBlock as ComponentType<XbotBlockProps> })));

/** The blocks of an agent.message/1 answer. */
export const XBOT_BLOCK_REGISTRY: XbotBlockRegistry = new Map<string, XbotBlockComponent>([
  ["value", ValueBlock as ComponentType<XbotBlockProps>],
  ["table", TableBlock as ComponentType<XbotBlockProps>],
  ["comparison", ComparisonBlock as ComponentType<XbotBlockProps>],
  ["timeseries", TimeseriesBlock],
  ["distribution", DistributionBlock],
  ["relations", RelationsBlock],
  ["text", TextBlock as ComponentType<XbotBlockProps>],
  ["question", QuestionBlock as ComponentType<XbotBlockProps>],
]);

const NO_BLOCKS: XbotBlockRegistry = new Map<string, XbotBlockComponent>();

// Only agent.message/1 is drawn; a later major version could be misread, so it falls back to fallback_text.
export function registryFor(schemaVersion: string | null | undefined): XbotBlockRegistry {
  return schemaVersion === XBOT_MESSAGE_VERSION ? XBOT_BLOCK_REGISTRY : NO_BLOCKS;
}

const NOTHING_TO_SHOW = "This part of the answer cannot be shown here.";

const KNOWN_TYPES: readonly string[] = XBOT_BLOCK_TYPES;

function fallbackOf(block: XbotMessageBlock): string {
  return typeof block.fallback_text === "string" && block.fallback_text.trim() ? block.fallback_text : "";
}

// Shown when the block itself can't be drawn; keeps the tone's head (refusal, no data) too, and links the names the
// block's words link (its `links`).
export const FallbackText: FC<{ text: string; loading?: boolean; tone?: XbotTone; names?: XbotPageNames | null }> = ({
  text, loading, tone, names,
}) => (
  <div className={loading ? "xm-xbot-fallback is-loading" : "xm-xbot-fallback"}>
    {text && tone && tone !== "normal"
      ? <TonedPlainText text={text} tone={tone} names={names} />
      : text ? namedNodes(text, names) : NOTHING_TO_SHOW}
  </div>
);

interface BoundaryProps {
  fallback: ReactNode;
  children: ReactNode;
}

// Class component on purpose: only a class can catch a render error in React.
class BlockBoundary extends Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

/** What a failure says: the service's own words, or a plain sentence when it sent none. */
export function errorText(error: XbotErrorBody | null | undefined, fallback = "This part of the answer could not be finished."): string {
  return error?.message?.trim() || fallback;
}

/** How many grey pieces a coming block is drawn with: a table's rows, a chart's bars. */
const SHAPE_PIECES: Record<string, number> = { table: 4, comparison: 2, distribution: 5, relations: 3, text: 3 };

/**
 * A coming block is drawn in the shape it will have, so it shows what is on
 * its way: a big number, rows of a table, a chart's area, bars, a tree, lines of text.
 */
export const PendingShape: FC<{ type: string }> = ({ type }) => {
  const known = type === "value" || type === "timeseries" || type in SHAPE_PIECES;
  const shape = known ? type : "text";
  const pieces = Array.from({ length: SHAPE_PIECES[shape] ?? 1 }, (_, at) => <span key={at} className="xm-xbot-shape-piece" />);
  return <div className={`xm-xbot-pending xm-xbot-shape xm-xbot-shape-${shape}`} aria-hidden="true" data-shape={shape}>
    {shape === "value" ? <><span className="xm-xbot-shape-piece is-number" /><span className="xm-xbot-shape-piece" /></> : pieces}
  </div>;
};

function BlockBody({ block, host, registry, settled, tone }: XbotBlockViewProps): ReactElement {
  const fallback = fallbackOf(block);
  const look = tone?.tone;
  const names = blockNames(block, host.onOpenLink);
  if (block.state === "pending") {
    if (settled) {
      // The turn ended before this block did.
      return <div className="xm-xbot-unfinished">
        <div>This part was not finished.</div>
        {fallback ? <FallbackText text={fallback} names={names} /> : null}
      </div>;
    }
    return <PendingShape type={String(block.type)} />;
  }
  if (block.state === "error") {
    return <div className="xm-xbot-error">
      <div role="alert">{errorText(block.error)}</div>
      {fallback ? <FallbackText text={fallback} names={names} /> : null}
    </div>;
  }
  const Draw = block.state === "ready" ? registry.get(block.type) : undefined;
  if (!Draw || !block.config || typeof block.config !== "object") {
    return <FallbackText text={fallback} tone={look} names={names} />;
  }
  return <BlockBoundary key={block.state} fallback={<FallbackText text={fallback} tone={look} names={names} />}>
    <Suspense fallback={<FallbackText text={fallback} loading tone={look} names={names} />}>
      <Draw block={block} host={host} tone={tone} />
    </Suspense>
  </BlockBoundary>;
}

export interface XbotBlockViewProps {
  block: XbotMessageBlock;
  host: XbotBlockHost;
  registry: XbotBlockRegistry;
  /** turn is over; a still-pending block will not arrive */
  settled: boolean;
  tone?: XbotBlockTone;
  /**
   * A long answer (XbotAnswer.tsx): true draws only the title and the block's one sentence,
   * false the whole block; its title then folds and unfolds it. Absent: the block cannot fold.
   */
  folded?: boolean;
  onFold?: (folded: boolean) => void;
}

export function blockLook(block: XbotMessageBlock, tone: XbotBlockTone | undefined): XbotTone | null {
  if (block.state === "error") {
    return "error";
  }
  return tone && tone.tone !== "normal" ? tone.tone : null;
}

// The title of a block that folds: a press shows the whole block or folds it back to its one sentence.
const FoldTitle: FC<{ title: string; folded: boolean; onFold?: (folded: boolean) => void }> = ({ title, folded, onFold }) => (
  <button type="button" className="xm-xbot-block-title xm-xbot-fold-title" aria-expanded={!folded} onClick={() => onFold?.(!folded)}>
    {folded ? <LuChevronRight aria-hidden="true" /> : <LuChevronDown aria-hidden="true" />}
    <span>{title}</span>
  </button>
);

/** What a folded part's card offers to show, by what the part draws: "Show chart". */
export function showWord(type: string): string {
  return DATA_BLOCK_TYPES.includes(type) ? `Show ${type === "timeseries" ? "chart" : type}` : "Show";
}

// A folded part as one card to press anywhere: its title with a chevron, what a press shows, and its sentence.
const FoldCard: FC<{ title: string; type: string; gist: string; onFold?: (folded: boolean) => void }> = ({ title, type, gist, onFold }) => (
  <button type="button" className="xm-xbot-fold-card" aria-expanded={false} onClick={() => onFold?.(false)}>
    <span className="xm-xbot-fold-card-head">
      <LuChevronRight aria-hidden="true" className="xm-xbot-fold-card-chevron" />
      <span className="xm-xbot-fold-card-title">{title}</span>
      <span className="xm-xbot-fold-card-show">{showWord(type)}</span>
    </span>
    {gist ? <span className="xm-xbot-fold-gist">{gist}</span> : null}
  </button>
);

export const XbotBlockView: FC<XbotBlockViewProps> = (props) => {
  const { block, settled, tone } = props;
  const type = String(block.type);
  const known = KNOWN_TYPES.includes(type);
  const badges = Array.isArray(block.badges) ? block.badges : [];
  const busy = block.state === "pending" && !settled;
  const look = blockLook(block, tone);
  // A title's words are printed, not its marks: "**Peak load:**" reads "Peak load:".
  const own = known ? `xm-xbot-block xm-xbot-block-${type}` : "xm-xbot-block";
  const foldable = props.folded !== undefined && props.onFold !== undefined;
  const titleWords = plainMarks(block.title || "");
  if (foldable && props.folded) {
    return <section className={`${own} is-folded`} data-block-id={block.id} data-block-type={type} data-state={block.state}>
      <FoldCard title={titleWords || type} type={type} gist={fallbackOf(block)} onFold={props.onFold} />
    </section>;
  }
  const content = <>
    {block.title && foldable ? <FoldTitle title={titleWords} folded={false} onFold={props.onFold} /> : null}
    {/* a title's names open their pages (its block's `links`); a folded title is a button, so its words stay plain */}
    {block.title && !foldable
      ? <div className="xm-xbot-block-title">{namedNodes(titleWords, blockNames(block, props.host.onOpenLink))}</div>
      : null}
    {badges.length ? <BadgeLead badges={badges} /> : null}
    <BlockBody {...props} />
    {badges.length ? <BadgeNotes badges={badges} /> : null}
  </>;
  return <section
    className={look ? `${own} ${calloutClass(look)}` : own}
    data-block-id={block.id}
    data-block-type={type}
    data-state={block.state}
    data-tone={look || undefined}
    aria-busy={busy || undefined}
  >
    {look ? <><CalloutIcon tone={look} /><div className="xm-xbot-callout-body">{content}</div></> : content}
  </section>;
};
